import { toast } from "react-toastify";

/**
 * The rules for an image picked in a form (#10), the same ones `CommonService.UploadImage` applies
 * on the server: a .jpg, .jpeg or .png file of at most 2 MB.
 *
 * The client checks them first so a file the server would refuse never leaves the browser. The
 * server stays the authority; these sentences are its own (`ConstantValue.ImgExtn` / `ImgSize`), so
 * a refusal reads the same whichever side made it.
 */

/** For an `<input type="file">`'s `accept`: the file picker offers only these. */
export const IMAGE_ACCEPT = ".jpg,.jpeg,.png";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];

/** The server compares the byte count with `2 * 1024 * 1024`, so exactly 2 MB is allowed. */
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

export const IMAGE_TYPE_ERROR = "Invalid file type. Only JPEG, JPG, and PNG files are allowed.";
export const IMAGE_SIZE_ERROR =
  "File size exceeds the limit. Please upload an image with a size of up to 2 MB.";

/**
 * The hint under every image field (#308), the same words on every form. It used to be typed into
 * each one, as "(jpeg, jpg, png and size upto 2 mb)" or "... up to 2 mb", some with a red required
 * mark although no image is required.
 */
export const IMAGE_HINT = "JPEG, JPG or PNG, up to 2 MB";

/** Shown when Save is pressed while the picked image is still on its way to the server. */
export const IMAGE_STILL_UPLOADING = "The image is still uploading. Save again once it has finished.";

/**
 * Why the server would refuse this file, or null when it would take it.
 *
 * @param {{name?: string, size?: number} | null | undefined} file A File from a file input.
 * @returns {string | null} The refusal sentence, or null (also when there is no file).
 */
export const imageFileError = (file) => {
  if (!file) return null;

  const name = String(file.name ?? "").toLowerCase();
  const dot = name.lastIndexOf(".");
  const extension = dot >= 0 ? name.slice(dot) : "";
  if (!IMAGE_EXTENSIONS.includes(extension)) return IMAGE_TYPE_ERROR;

  if (file.size > MAX_IMAGE_BYTES) return IMAGE_SIZE_ERROR;

  return null;
};

/**
 * Checks the file just picked in `input` before anything shows it (#251). A file the server would
 * refuse is reported with its sentence and the input is emptied, so re-picking it reports it again;
 * the caller must then leave its preview and file state alone.
 *
 * @param {HTMLInputElement | null | undefined} input The file input that changed.
 * @returns {boolean} True when there is nothing to upload: no file (a cancelled picker) or a refused one.
 */
export const rejectImagePick = (input) => {
  const file = input?.files?.[0];
  if (!file) return true;

  const error = imageFileError(file);
  if (!error) return false;

  toast.error(error);
  input.value = "";
  return true;
};
